
// Cancel/withdraw a PENDING permission request.
export async function cancelPermission(id) {
  return apiCall("PUT", `/permission/cancel/${encodeURIComponent(id)}`);
}

// Cancel/withdraw a PENDING attendance-correction request.
export async function cancelCorrection(id) {
  return apiCall("PUT", `/attendance/correction/cancel/${encodeURIComponent(id)}`);
}

//  * Attendance & Leave api Service/
//  * ================================
//  * Talks to the dedicated Attendance API Gateway.
//  * Uses the same Microsoft SSO ID token as the CTODOCX and Platform APIs
//  * (same cto-platform-authorizer validates it).
//  *
//  * Base URL: REACT_APP_ATTENDANCE_API_BASE_URL
//  * All routes match the backend "Guide 3 — Lambda & API Gateway Setup".
//  */

import { msalConfig } from "../authConfig";
import { PublicClientApplication } from "@azure/msal-browser";

const ATTENDANCE_API_BASE = process.env.REACT_APP_ATTENDANCE_API_BASE_URL || "";

let msalInstance = null;

export function initAttendanceApi(instance) {
  msalInstance = instance;
}

// Guard so multiple parallel API calls never trigger loginRedirect twice
// (prevents MSAL "interaction_in_progress" errors).
let redirecting = false;

function safeLoginRedirect() {
  if (redirecting) return;
  redirecting = true;
  try {
    msalInstance.loginRedirect({ scopes: ["openid", "profile", "email"] });
  } catch (e) {
    redirecting = false;
  }
}

async function getToken() {
  // IMPORTANT: never create a new PublicClientApplication here.
  // A second instance has no active account/cache and triggers an endless
  // loginRedirect/refresh loop. Wait for initAttendanceApi(instance) to set
  // the shared MSAL instance (done synchronously by AttendanceLeaveApp).
  if (!msalInstance) {
    throw new Error("Attendance API not ready yet. Please retry.");
  }

  // Resolve an account: active first, then any cached account.
  let account = msalInstance.getActiveAccount();
  if (!account) {
    const all = msalInstance.getAllAccounts();
    if (all && all.length > 0) {
      account = all[0];
      msalInstance.setActiveAccount(account);
    }
  }
  if (!account) {
    safeLoginRedirect();
    throw new Error("Session expired. Redirecting to login...");
  }

  try {
    const resp = await msalInstance.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account,
    });
    return resp.idToken;
  } catch (silentErr) {
    // Silent failed — token truly expired. Redirect ONCE (guarded).
    // Do NOT use popup+redirect together (that causes interaction_in_progress).
    safeLoginRedirect();
    throw new Error("Session expired. Please sign in again.");
  }
}

async function apiCall(method, path, body = null) {
  const token = await getToken();
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
  if (body) options.body = JSON.stringify(body);

  const res = await fetch(`${ATTENDANCE_API_BASE}${path}`, options);
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = { message: text };
  }
  if (!res.ok) {
    throw new Error(data.error || data.message || `API Error: ${res.status}`);
  }
  return data;
}

// ═══════════════════════════════════════════════
// ATTENDANCE (Employee)
// ═══════════════════════════════════════════════

export async function checkIn(isWfh = false) {
  return apiCall("POST", "/attendance/check-in", isWfh ? { is_wfh: true } : null);
}

export async function checkOut() {
  return apiCall("POST", "/attendance/check-out");
}

export async function getToday() {
  return apiCall("GET", "/attendance/today");
}

export async function getMonthly(month, year) {
  return apiCall("GET", `/attendance/monthly?month=${month}&year=${year}`);
}

// Public settings — shift window etc. (employee-accessible, read-only).
export async function getPublicSettings() {
  return apiCall("GET", "/settings/public");
}

// Company holidays for a given year (employee-accessible).
export async function getHolidays(year) {
  return apiCall("GET", year ? `/holidays?year=${year}` : "/holidays");
}

// ═══════════════════════════════════════════════
// LEAVE (Employee)
// ═══════════════════════════════════════════════

export async function applyLeave(payload) {
  // Frontend sends camelCase; backend expects snake_case + duration.
  const body = {
    leave_type: payload.leaveType,
    from_date: payload.fromDate,
    to_date: payload.toDate || payload.fromDate,
    duration: payload.isHalfDay ? "HALF_DAY" : "FULL_DAY",
    reason: payload.reason,
  };
  if (payload.isHalfDay && payload.halfSession) {
    body.half_session = payload.halfSession;
  }
  return apiCall("POST", "/leaves/apply", body);
}

export async function getLeaveBalance() {
  return apiCall("GET", "/leaves/balance");
}

export async function getLeaveHistory() {
  return apiCall("GET", "/leaves/history");
}

export async function cancelLeave(leaveId) {
  return apiCall("PUT", `/leaves/cancel/${encodeURIComponent(leaveId)}`);
}

// ═══════════════════════════════════════════════
// PERMISSION (Employee)
// ═══════════════════════════════════════════════

export async function applyPermission(payload) {
  // Backend expects from_time/to_time as full ISO datetimes (IST, +05:30).
  // Frontend gives permDate ("2026-09-30") + fromTime/toTime ("16:00").
  const toISO = (date, time) => {
    if (!date || !time) return null;
    const t = time.length === 5 ? `${time}:00` : time; // HH:MM -> HH:MM:SS
    return `${date}T${t}+05:30`;
  };
  const body = {
    from_time: toISO(payload.permDate, payload.fromTime),
    to_time: toISO(payload.permDate, payload.toTime),
    reason: payload.reason,
  };
  return apiCall("POST", "/permission/apply", body);
}

// Employee permission request history.
export async function getPermissionHistory() {
  return apiCall("GET", "/permission/history");
}

// ═══════════════════════════════════════════════
// CORRECTION (Employee)
// ═══════════════════════════════════════════════

export async function requestCorrection(payload) {
  const body = {
    work_date: payload.workDate,
    requested_check_in: payload.requestedCheckIn || null,
    requested_check_out: payload.requestedCheckOut || null,
    reason: payload.reason,
  };
  return apiCall("POST", "/attendance/correction", body);
}

// Employee attendance-correction request history.
export async function getCorrectionHistory() {
  return apiCall("GET", "/attendance/correction/history");
}

// ═══════════════════════════════════════════════
// ADMIN — Records
// ═══════════════════════════════════════════════

export async function adminGetToday() {
  return apiCall("GET", "/admin/attendance/today");
}

export async function adminGetUserMonthly(userId, month, year) {
  return apiCall("GET", `/admin/attendance/user/${encodeURIComponent(userId)}?month=${month}&year=${year}`);
}

export async function adminEditAttendance(recordId, updates) {
  const body = {};
  if (updates.checkInTime !== undefined || updates.check_in !== undefined)
    body.check_in = updates.checkInTime ?? updates.check_in ?? null;
  if (updates.checkOutTime !== undefined || updates.check_out !== undefined)
    body.check_out = updates.checkOutTime ?? updates.check_out ?? null;
  if (updates.status !== undefined) body.status = updates.status;
  if (updates.isWfh !== undefined || updates.is_wfh !== undefined)
    body.is_wfh = updates.isWfh ?? updates.is_wfh;
  return apiCall("PUT", `/admin/attendance/${encodeURIComponent(recordId)}`, body);
}

// ═══════════════════════════════════════════════
// ADMIN — Approvals
// ═══════════════════════════════════════════════

export async function adminGetLeaveRequests(status) {
  return apiCall("GET", `/admin/leaves/requests${status ? `?status=${encodeURIComponent(status)}` : ""}`);
}

// Map frontend decision -> backend status
function toStatus(decision) {
  const d = String(decision || "").toUpperCase();
  if (d === "APPROVE" || d === "APPROVED") return "APPROVED";
  if (d === "REJECT" || d === "REJECTED") return "REJECTED";
  return d;
}

export async function adminReviewLeave(leaveId, decision, note = "") {
  return apiCall("PUT", `/admin/leaves/${encodeURIComponent(leaveId)}`, { status: toStatus(decision), review_note: note });
}

export async function adminGetCorrections(status) {
  return apiCall("GET", `/admin/corrections${status ? `?status=${encodeURIComponent(status)}` : ""}`);
}

export async function adminReviewCorrection(correctionId, decision, note = "") {
  return apiCall("PUT", `/admin/corrections/${encodeURIComponent(correctionId)}`, { status: toStatus(decision), review_note: note });
}

export async function adminGetPermissionRequests(status) {
  return apiCall("GET", `/admin/permissions/requests${status ? `?status=${encodeURIComponent(status)}` : ""}`);
}

export async function adminReviewPermission(permissionId, decision, note = "") {
  return apiCall("PUT", `/admin/permissions/${encodeURIComponent(permissionId)}`, { status: toStatus(decision), review_note: note });
}

// WFH approval requests (admin).
// WFH is now an APPROVAL flow: an employee check-in with WFH requested stays
// PENDING until an admin approves it. Backend routes are being added in
// parallel:
//   GET /admin/wfh/requests        -> list WFH requests (optional ?status=)
//   PUT /admin/wfh/{id}            -> { status: APPROVED | REJECTED }
// Callers MUST invoke these defensively (try/catch) and hide the WFH UI if
// the route 404s until the backend ships.
export async function adminGetWfhRequests(status) {
  return apiCall("GET", `/admin/wfh/requests${status ? `?status=${encodeURIComponent(status)}` : ""}`);
}

export async function adminReviewWfh(id, decision, note = "") {
  return apiCall("PUT", `/admin/wfh/${encodeURIComponent(id)}`, { status: toStatus(decision), review_note: note });
}

export async function adminGetLeaveBalances(userId) {
  return apiCall("GET", `/admin/leave-balances/${encodeURIComponent(userId)}`);
}

// ═══════════════════════════════════════════════
// ADMIN — Settings
// ═══════════════════════════════════════════════

export async function adminGetSettings() {
  return apiCall("GET", "/admin/settings");
}

export async function adminUpdateSettings(settings) {
  // Convert camelCase -> snake_case; backend whitelists these keys.
  const map = {
    shiftStart: "shift_start", shiftEnd: "shift_end",
    graceMinutes: "grace_minutes", breakMinutes: "break_minutes",
    halfDayHours: "half_day_hours", fullDayHours: "full_day_hours",
    permissionMonthlyLimit: "permission_monthly_limit",
    clQuota: "cl_quota", slQuota: "sl_quota", elQuota: "el_quota",
    lateAlertMinutes: "late_alert_minutes",
  };
  const body = {};
  Object.entries(settings || {}).forEach(([k, v]) => {
    body[map[k] || k] = v; // map known keys; pass through if already snake_case
  });
  return apiCall("PUT", "/admin/settings", body);
}

export async function adminGetLeaveQuota(year) {
  const y = year || new Date().getFullYear();
  return apiCall("GET", `/admin/leave-quota?year=${encodeURIComponent(y)}`);
}

export async function adminSetLeaveQuota(payload) {
  const body = {
    year: payload.year,
    cl_quota: payload.clQuota ?? payload.cl_quota,
    sl_quota: payload.slQuota ?? payload.sl_quota,
    el_quota: payload.elQuota ?? payload.el_quota,
  };
  return apiCall("PUT", "/admin/leave-quota", body);
}

export async function adminAddHoliday(payload) {
  const body = {
    holiday_date: payload.holidayDate ?? payload.holiday_date,
    name: payload.name,
    type: payload.type,
    is_working: (payload.isWorking ?? payload.is_working) === true,
  };
  return apiCall("POST", "/admin/holidays", body);
}

// List holidays for a given year (admin). Falls back to all when no year.
export async function adminGetHolidays(year) {
  return apiCall("GET", year ? `/admin/holidays?year=${encodeURIComponent(year)}` : "/admin/holidays");
}

// Update an existing holiday (admin). payload may include any of:
// name, type, is_working/isWorking, holiday_date/holidayDate.
export async function adminUpdateHoliday(id, payload) {
  const body = {};
  if (payload.name !== undefined) body.name = payload.name;
  if (payload.type !== undefined) body.type = payload.type;
  if (payload.isWorking !== undefined || payload.is_working !== undefined)
    body.is_working = (payload.isWorking ?? payload.is_working) === true;
  if (payload.holidayDate !== undefined || payload.holiday_date !== undefined)
    body.holiday_date = payload.holidayDate ?? payload.holiday_date;
  return apiCall("PUT", `/admin/holidays/${encodeURIComponent(id)}`, body);
}

// Delete a holiday (admin).
export async function adminDeleteHoliday(id) {
  return apiCall("DELETE", `/admin/holidays/${encodeURIComponent(id)}`);
}

// ═══════════════════════════════════════════════
// ADMIN — Reports
// ═══════════════════════════════════════════════

// ═══════════════════════════════════════════════
// REPORT DOWNLOAD (binary blob) — PDF / Excel
// The backend returns a real file (binary), not a JSON download URL.
// Fetch with auth, read as blob, and trigger a browser download.
// ═══════════════════════════════════════════════

// Map a report format to the MIME type the browser (and API Gateway binary
// media conversion) expects. API Gateway only base64-decodes the response
// into a real binary file when the request Accept header matches one of the
// configured binaryMediaTypes — otherwise it returns base64 text and the
// downloaded file is corrupt. So we MUST send a format-matching Accept header.
function acceptForFormat(format) {
  const f = String(format || "").toLowerCase();
  if (f === "pdf") return "application/pdf";
  if (f === "excel" || f === "xlsx")
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  // Unknown format — accept anything so the download still works.
  return "*/*";
}

async function downloadReportBlob(path, fallbackName, format) {
  const token = await getToken();
  const res = await fetch(`${ATTENDANCE_API_BASE}${path}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: acceptForFormat(format),
    },
  });
  if (!res.ok) {
    // Try to surface a JSON error message if the server sent one.
    let msg = `Report failed: ${res.status}`;
    try {
      const j = await res.json();
      msg = j.error || j.message || msg;
    } catch (_) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  // Filename from Content-Disposition if present, else fallback.
  let name = fallbackName;
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/i);
  if (m) name = m[1];
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Admin report — download as a real file (pdf/excel).
export async function downloadAdminReport(format, filters = {}) {
  const params = new URLSearchParams({ format, ...filters }).toString();
  const ext = format === "excel" ? "xlsx" : "pdf";
  return downloadReportBlob(`/admin/report?${params}`, `attendance-report.${ext}`, format);
}

// Employee own report — download as a real file (pdf/excel).
export async function downloadMyReport(format, month, year) {
  const ext = format === "excel" ? "xlsx" : "pdf";
  return downloadReportBlob(
    `/report/my?format=${format}&month=${month}&year=${year}`,
    `my-attendance-${month}-${year}.${ext}`,
    format
  );
}
